import { useMemo, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { MobileDrawer } from "@/components/MobileDrawer";
import { Footer } from "@/components/Footer";
import ChatbotWidget from "@/components/ui/ChatbotWidget";
import { ScrollToTop } from "@/components/ScrollToTop";
import { getBlogHomeUrl } from "@/lib/blogUrls";
import { Home } from "@/pages/Home";
import { AboutPage } from "@/pages/AboutPage";
import { ContactPage } from "@/pages/ContactPage";
import { CareersPage } from "@/pages/CareersPage";
import ServicesPage from "@/pages/ServicesPage";
import { ServiceCategoryPage } from "@/pages/ServiceCategoryPage";
import WorkPage from "@/pages/WorkPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { TermsPage } from "@/pages/TermsPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { SupportApp } from "@/SupportApp";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { tNav } from "@/lib/siteCopy";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

export default function App() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const lang = useSiteLanguage();

  const isSupportArea = location.pathname.startsWith("/support");

  const navItems: NavItem[] = useMemo(
    () => [
      { label: tNav(lang, "Home"), path: "/", type: "route" },
      {
        label: tNav(lang, "Services"),
        path: "/services",
        id: "services",
        type: "route",
      },
      {
        label: tNav(lang, "Blog"),
        href: getBlogHomeUrl(),
        id: "blog",
        type: "external",
      },
      { label: tNav(lang, "Work"), path: "/work", type: "route" },
      { label: tNav(lang, "About"), path: "/about", type: "route" },
      { label: tNav(lang, "Careers"), path: "/careers", type: "route" },
      {
        label: tNav(lang, "Contact"),
        path: "/contact",
        id: "contact",
        type: "route",
      },
    ],
    [lang],
  );

  if (isSupportArea) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/support/*" element={<SupportApp />} />
        </Routes>
      </>
    );
  }

  const isServicesPage =
    location.pathname === "/services" ||
    location.pathname.startsWith("/services/");

  const isWorkPage =
    location.pathname === "/work" || location.pathname.startsWith("/work/");

  const isCompactHeaderPage = isServicesPage || isWorkPage;

  const knownRoots = [
    "/",
    "/services",
    "/about",
    "/contact",
    "/careers",
    "/products",
    "/work",
    "/privacy",
    "/terms",
  ];

  const is404Page =
    !knownRoots.includes(location.pathname) &&
    !location.pathname.startsWith("/services/") &&
    !location.pathname.startsWith("/work/");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ScrollToTop />
      {!isCompactHeaderPage && !is404Page && (
        <Header
          onMobileMenuOpen={() => setDrawerOpen(true)}
          navItems={navItems}
        />
      )}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/services"
            element={
              <ServicesPage
                navItems={navItems}
                onMobileMenuOpen={() => setDrawerOpen(true)}
              />
            }
          />
          <Route
            path="/services/:categorySlug"
            element={
              <ServiceCategoryPage
                navItems={navItems}
                onMobileMenuOpen={() => setDrawerOpen(true)}
              />
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route
            path="/work"
            element={
              <WorkPage
                navItems={navItems}
                onMobileMenuOpen={() => setDrawerOpen(true)}
              />
            }
          />
          <Route
            path="/work/:id"
            element={
              <ProjectDetailPage
                navItems={navItems}
                onMobileMenuOpen={() => setDrawerOpen(true)}
              />
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {!is404Page && <Footer />}
      {!is404Page && <ChatbotWidget />}
      {!is404Page && (
        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          navItems={navItems}
        />
      )}
    </div>
  );
}
